import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useStore } from '../store/useStore'
import { api } from '../lib/api'
import LanguageSelector from '../components/LanguageSelector'
import { useAlert } from '../components/AlertProvider'

// 개발 환경 감지
const IS_DEV = import.meta.env.DEV

export default function LoginPage() {
  const { user, setUser, setToken } = useStore()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { showAlert } = useAlert()
  const [loading, setLoading] = useState(false)
  const [mode, setMode] = useState<'login' | 'register'>('login')
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    username: ''
  })

  useEffect(() => {
    if (user) {
      navigate('/', { replace: true })
    }
  }, [user, navigate])

  if (user) return null

  // 이메일/비밀번호 로그인
  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Login failed')
      }

      const data = await response.json()
      setUser(data.user)
      setToken(data.token)
      api.setToken(data.token)
      navigate('/')
    } catch (error) {
      console.error('Login failed:', error)
      showAlert(error instanceof Error ? error.message : t('common.error'))
    } finally {
      setLoading(false)
    }
  }

  // 회원가입
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          username: formData.username
        }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Registration failed')
      }

      const data = await response.json()
      setUser(data.user)
      setToken(data.token)
      api.setToken(data.token)
      navigate('/')
    } catch (error) {
      console.error('Registration failed:', error)
      showAlert(error instanceof Error ? error.message : t('common.error'))
    } finally {
      setLoading(false)
    }
  }

  // 개발용 로그인
  const handleDevLogin = async () => {
    setLoading(true)
    try {
      const response = await fetch('/api/auth/dev', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })

      if (!response.ok) {
        throw new Error('Login failed')
      }

      const data = await response.json()
      setUser(data.user)
      setToken(data.token)
      api.setToken(data.token)
      navigate('/')
    } catch (error) {
      console.error('Dev login failed:', error)
      showAlert(t('common.error'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen min-h-[100dvh] flex flex-col relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-coral-50 via-base-100 to-mint-50">
        {/* Floating decorative elements */}
        <div className="floating-blob floating-blob-coral w-96 h-96 -top-20 -left-20 opacity-40" />
        <div className="floating-blob floating-blob-mint w-80 h-80 top-1/3 -right-20 opacity-40" />
        <div className="floating-blob floating-blob-purple w-72 h-72 -bottom-20 left-1/4 opacity-30" />
        <div className="floating-blob floating-blob-coral w-64 h-64 bottom-1/4 right-1/4 opacity-30" />
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-6 relative z-10">
        <div className="w-full max-w-md">
          {/* Logo & Branding */}
          <div className="text-center mb-8 fade-in-up">
            <div className="inline-block mb-6">
              <div className="relative">
                <div className="w-28 h-28 mx-auto rounded-3xl bg-gradient-to-br from-coral-400 to-mint-400 flex items-center justify-center shadow-2xl transform hover:scale-105 transition-transform duration-300">
                  <span className="text-6xl">💝</span>
                </div>
                {/* Glow effect */}
                <div className="absolute inset-0 w-28 h-28 mx-auto rounded-3xl bg-gradient-to-br from-coral-400 to-mint-400 blur-2xl opacity-40 -z-10" />
              </div>
            </div>

            <h1 className="text-4xl font-bold text-gradient-premium mb-3">
              {t('app.title', 'Glovely')}
            </h1>
            <p className="text-base-content/60 text-lg">
              {t('auth.tagline', '전 세계에서 특별한 인연을 만나보세요')}
            </p>

            {/* Language Selector */}
            <div className="mt-4">
              <LanguageSelector />
            </div>
          </div>

          {/* Auth Card - Glassmorphism */}
          <div className="glass-card p-6 md:p-8 fade-in-scale">
            {/* Tab Selector */}
            <div className="flex bg-base-200/50 rounded-2xl p-1 mb-6">
              <button
                onClick={() => setMode('login')}
                className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold transition-all ${mode === 'login'
                    ? 'bg-white text-primary shadow-md'
                    : 'text-base-content/60 hover:text-base-content'
                  }`}
              >
                {t('auth.login')}
              </button>
              <button
                onClick={() => setMode('register')}
                className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold transition-all ${mode === 'register'
                    ? 'bg-white text-primary shadow-md'
                    : 'text-base-content/60 hover:text-base-content'
                  }`}
              >
                {t('auth.register')}
              </button>
            </div>

            {/* Login Form */}
            {mode === 'login' && (
              <form onSubmit={handleEmailLogin} className="space-y-5">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-base-content/70">
                    {t('auth.email')}
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-base-content/40">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </span>
                    <input
                      type="email"
                      placeholder={t('auth.emailPlaceholder', 'your@email.com')}
                      className="input input-bordered w-full pl-12 h-12 rounded-xl bg-white/50 focus:bg-white focus:border-primary transition-all"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-base-content/70">
                    {t('auth.password')}
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-base-content/40">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </span>
                    <input
                      type="password"
                      placeholder="••••••"
                      className="input input-bordered w-full pl-12 h-12 rounded-xl bg-white/50 focus:bg-white focus:border-primary transition-all"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-premium w-full h-12 text-base"
                >
                  {loading ? (
                    <span className="loading loading-spinner loading-sm" />
                  ) : (
                    <>
                      <span>{t('auth.loginButton')}</span>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Register Form */}
            {mode === 'register' && (
              <form onSubmit={handleRegister} className="space-y-5">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-base-content/70">
                    {t('auth.email')}
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-base-content/40">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </span>
                    <input
                      type="email"
                      placeholder={t('auth.emailPlaceholder', 'your@email.com')}
                      className="input input-bordered w-full pl-12 h-12 rounded-xl bg-white/50 focus:bg-white focus:border-primary transition-all"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-base-content/70">
                    {t('auth.nicknameOptional')}
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-base-content/40">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </span>
                    <input
                      type="text"
                      placeholder={t('auth.nicknamePlaceholder')}
                      className="input input-bordered w-full pl-12 h-12 rounded-xl bg-white/50 focus:bg-white focus:border-primary transition-all"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-base-content/70">
                    {t('auth.password')}
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-base-content/40">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </span>
                    <input
                      type="password"
                      placeholder={t('auth.passwordPlaceholder')}
                      className="input input-bordered w-full pl-12 h-12 rounded-xl bg-white/50 focus:bg-white focus:border-primary transition-all"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      minLength={6}
                    />
                  </div>
                  <p className="text-xs text-base-content/50">
                    {t('auth.passwordHint', '최소 6자 이상 입력해주세요')}
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-premium w-full h-12 text-base"
                >
                  {loading ? (
                    <span className="loading loading-spinner loading-sm" />
                  ) : (
                    <>
                      <span>{t('auth.registerButton')}</span>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Dev Login */}
            {IS_DEV && (
              <>
                <div className="flex items-center gap-3 my-5">
                  <div className="flex-1 h-px bg-base-200" />
                  <span className="text-xs text-base-content/40">{t('auth.devMode')}</span>
                  <div className="flex-1 h-px bg-base-200" />
                </div>
                <button
                  onClick={handleDevLogin}
                  disabled={loading}
                  className="btn btn-outline btn-sm w-full rounded-xl border-dashed"
                >
                  {loading ? (
                    <span className="loading loading-spinner loading-sm" />
                  ) : (
                    <>
                      <span className="text-lg mr-2">🚀</span>
                      {t('auth.devLogin')}
                    </>
                  )}
                </button>
              </>
            )}
          </div>

          {/* Footer */}
          <p className="text-center text-xs text-base-content/40 mt-6 px-4">
            {t('auth.agreeTerms', '가입 시')}{' '}
            <span className="underline cursor-pointer hover:text-primary transition-colors">
              {t('auth.termsOfService')}
            </span>{' '}
            {t('common.and', '및')}{' '}
            <span className="underline cursor-pointer hover:text-primary transition-colors">
              {t('auth.privacyPolicy')}
            </span>
            {t('auth.agreeTermsSuffix', '에 동의합니다')}
          </p>
        </div>
      </div>

      {/* Decorative Bottom Wave */}
      <div className="relative h-20 z-10">
        <svg className="absolute bottom-0 w-full" viewBox="0 0 1440 100" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M0 50C240 90 480 100 720 80C960 60 1200 20 1440 50V100H0V50Z"
            fill="url(#wave-gradient)"
            fillOpacity="0.3"
          />
          <defs>
            <linearGradient id="wave-gradient" x1="0" y1="0" x2="1440" y2="0">
              <stop offset="0%" stopColor="#F08B7D" />
              <stop offset="50%" stopColor="#7ECEC8" />
              <stop offset="100%" stopColor="#A855F7" />
            </linearGradient>
          </defs>
        </svg>
      </div>
    </div>
  )
}
